import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
// Required for dnd-kit CSS transformations
// eslint-disable-next-line import/no-extraneous-dependencies
import { CSS } from '@dnd-kit/utilities';
import { useMediaQuery, useTheme } from '@mui/material';
import { DraggableChartGridProps } from './DraggableChartGrid.props';
import {
  GridCell,
  GridContainer,
  GridItemWrapper,
  ResizeHandleE,
  ResizeHandleS,
  ResizeHandleSE,
  ResizePreview,
} from './DraggableChartGrid.styles';
import {
  DraggableItemProps,
  DroppableCellProps,
  GridPosition,
  ItemLayout,
  ResponsiveCols,
} from './DraggableChartGrid.types';

const DraggableItem: FC<DraggableItemProps> = ({
  id,
  position,
  w,
  h,
  children,
  onResize,
  onLockToggle,
  cols,
  rows,
  minW = 1,
  minH = 1,
  maxW,
  maxH,
  locked = true,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { position, w, h },
    disabled: locked, // Disable drag when locked (locked = can resize but not drag)
  });

  const [isResizing, setIsResizing] = useState(false);
  const [previewSize, setPreviewSize] = useState<{ w: number; h: number } | null>(null);
  const resizeStartRef = React.useRef<{
    x: number;
    y: number;
    w: number;
    h: number;
    cellWidth: number;
    cellHeight: number;
  } | null>(null);
  const itemRef = React.useRef<HTMLDivElement>(null);

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const handleResizeStart = (e: React.MouseEvent, direction: 'se' | 'e' | 's') => {
    // Only allow resize if locked (locked = can resize but not drag)
    if (!locked) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);

    // Get actual grid cell dimensions accurately
    const gridContainer = itemRef.current?.parentElement;
    if (!gridContainer || !itemRef.current) {
      return;
    }

    // Calculate actual cell width accounting for gaps
    const containerWidth = gridContainer.clientWidth;
    const gap = 16; // theme.spacing(2) = 16px
    const totalGaps = (cols - 1) * gap;
    const gridCellWidth = (containerWidth - totalGaps) / cols;

    // Get actual cell height from the item itself
    const gridCellHeight = itemRef.current.offsetHeight / h;

    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      w,
      h,
      cellWidth: gridCellWidth,
      cellHeight: gridCellHeight,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeStartRef.current) {
        return;
      }

      const deltaX = moveEvent.clientX - resizeStartRef.current.x;
      const deltaY = moveEvent.clientY - resizeStartRef.current.y;

      // Start from the committed size (stored in ref, which updates as we resize)
      let newW = resizeStartRef.current.w;
      let newH = resizeStartRef.current.h;

      // Calculate preview size (what the user is targeting)
      let previewW = resizeStartRef.current.w;
      let previewH = resizeStartRef.current.h;

      if (direction === 'se' || direction === 'e') {
        // Calculate how many full cells we've moved
        const cellsMoved = deltaX / resizeStartRef.current.cellWidth;

        // Calculate preview size (continuous, shows what user is targeting)
        // Round to nearest integer for preview, but clamp to min/max
        const targetPreviewW = resizeStartRef.current.w + cellsMoved;
        previewW = Math.max(minW, Math.min(maxW || cols, Math.round(targetPreviewW)));

        // Only change when we've crossed a cell boundary (0.5 threshold for smoother UX)
        // This requires moving 50% of a cell before snapping to next column
        if (Math.abs(cellsMoved) >= 0.5) {
          const colsToAdd =
            cellsMoved > 0
              ? Math.floor(cellsMoved) // Moving right: add columns
              : Math.ceil(cellsMoved); // Moving left: subtract columns

          const targetW = resizeStartRef.current.w + colsToAdd;
          newW = Math.max(minW, Math.min(maxW || cols, targetW));
        }
      }

      if (direction === 'se' || direction === 's') {
        // Calculate how many full cells we've moved
        const cellsMoved = deltaY / resizeStartRef.current.cellHeight;

        // Calculate preview size (continuous, shows what user is targeting)
        // Round to nearest integer for preview, but clamp to min/max
        const targetPreviewH = resizeStartRef.current.h + cellsMoved;
        previewH = Math.max(minH, Math.min(maxH || rows, Math.round(targetPreviewH)));

        // Only change when we've crossed a cell boundary (0.5 threshold for smoother UX)
        if (Math.abs(cellsMoved) >= 0.5) {
          const rowsToAdd =
            cellsMoved > 0
              ? Math.floor(cellsMoved) // Moving down: add rows
              : Math.ceil(cellsMoved); // Moving up: subtract rows

          const targetH = resizeStartRef.current.h + rowsToAdd;
          newH = Math.max(minH, Math.min(maxH || rows, targetH));
        }
      }

      // Update preview size for visual feedback
      setPreviewSize({ w: previewW, h: previewH });

      // Only call onResize if size actually changed from the committed size
      if (onResize && (newW !== resizeStartRef.current.w || newH !== resizeStartRef.current.h)) {
        onResize(id, newW, newH);

        // Reset start position when size changes to allow smooth continuation
        // This prevents accumulation errors by resetting to current mouse position
        // minus the remainder after committing the change
        if (newW !== resizeStartRef.current.w) {
          const colsChanged = newW - resizeStartRef.current.w;
          resizeStartRef.current.w = newW;
          // Reset X: current position minus the remainder after committing colsChanged cells
          const remainderX = deltaX - colsChanged * resizeStartRef.current.cellWidth;
          resizeStartRef.current.x = moveEvent.clientX - remainderX;
        }
        if (newH !== resizeStartRef.current.h) {
          const rowsChanged = newH - resizeStartRef.current.h;
          resizeStartRef.current.h = newH;
          // Reset Y: current position minus the remainder after committing rowsChanged cells
          const remainderY = deltaY - rowsChanged * resizeStartRef.current.cellHeight;
          resizeStartRef.current.y = moveEvent.clientY - remainderY;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setPreviewSize(null);
      resizeStartRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Combine refs
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (itemRef.current !== node) {
        itemRef.current = node;
      }
      setNodeRef(node);
    },
    [setNodeRef]
  );

  return (
    <GridItemWrapper
      ref={combinedRef}
      style={style}
      isDragging={isDragging}
      isResizing={isResizing}
      gridColumn={position.x + 1}
      gridRow={position.y + 1}
      gridColumnSpan={w}
      gridRowSpan={h}
      data-locked={locked ? 'true' : 'false'}
      {...(!locked && attributes ? attributes : {})}
      {...(!locked && listeners ? listeners : {})}
      onMouseEnter={e => {
        // Show resize handles on hover (only if locked)
        if (locked) {
          const handles = e.currentTarget.querySelectorAll('.resize-handle');
          handles.forEach(handle => {
            handle.classList.add('visible');
          });
        }
      }}
      onMouseLeave={e => {
        if (!isResizing && locked) {
          const handles = e.currentTarget.querySelectorAll('.resize-handle');
          handles.forEach(handle => {
            handle.classList.remove('visible');
          });
        }
      }}>
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<{ locked?: boolean; onLockToggle?: () => void }>, {
          locked,
          ...(onLockToggle && { onLockToggle: () => onLockToggle(id) }),
        })
        : children}
      {locked && (
        <>
          <ResizeHandleSE
            className="resize-handle"
            onMouseDown={e => handleResizeStart(e, 'se')}
            onClick={e => e.stopPropagation()}
          />
          <ResizeHandleE
            className="resize-handle"
            onMouseDown={e => handleResizeStart(e, 'e')}
            onClick={e => e.stopPropagation()}
          />
          <ResizeHandleS
            className="resize-handle"
            onMouseDown={e => handleResizeStart(e, 's')}
            onClick={e => e.stopPropagation()}
          />
        </>
      )}
      {isResizing && previewSize && resizeStartRef.current && (
        <ResizePreview
          previewW={previewSize.w}
          previewH={previewSize.h}
          cellWidth={resizeStartRef.current.cellWidth}
          cellHeight={resizeStartRef.current.cellHeight}
        />
      )}
    </GridItemWrapper>
  );
};

const DroppableCell: FC<DroppableCellProps> = ({ id, position, isEmpty, isOver }) => {
  const { setNodeRef } = useDroppable({
    id,
    data: { position },
  });

  return (
    <GridCell
      ref={setNodeRef}
      isEmpty={isEmpty}
      isOver={isOver}
      style={{
        gridColumn: `${position.x + 1} / span 1`,
        gridRow: `${position.y + 1} / span 1`,
      }}
    />
  );
};

export const DraggableChartGrid: FC<DraggableChartGridProps> = ({
  children,
  className,
  onLayoutChange,
  cols = 12,
  rows: fixedRows,
  layouts: externalLayouts,
}) => {
  const theme = useTheme();
  const childrenArray = React.Children.toArray(children);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [calculatedRows, setCalculatedRows] = React.useState<number>(4);

  // Media queries must be called at component level (hooks rules)
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  // Determine current number of columns based on breakpoints
  const currentCols = useMemo(() => {
    if (typeof cols === 'number') {
      return cols;
    }

    // Responsive configuration
    const responsiveCols = cols as ResponsiveCols;

    if (isXl && responsiveCols.xl !== undefined) {
      return responsiveCols.xl;
    }
    if (isLg && responsiveCols.lg !== undefined) {
      return responsiveCols.lg;
    }
    if (isMd && responsiveCols.md !== undefined) {
      return responsiveCols.md;
    }
    if (isSm && responsiveCols.sm !== undefined) {
      return responsiveCols.sm;
    }
    if (responsiveCols.xs !== undefined) {
      return responsiveCols.xs;
    }

    // Default fallback
    return 12;
  }, [cols, isXl, isLg, isMd, isSm]);

  // Calculate rows dynamically if not provided
  React.useEffect(() => {
    if (fixedRows !== undefined) {
      // Use fixed rows if provided
      return;
    }

    const calculateRows = () => {
      // Wait for container to be rendered
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      // Calculate available height: viewport height minus container's top position
      // This accounts for any content above the grid and prevents vertical scroll
      const viewportHeight = window.innerHeight;
      const containerTop = containerRect.top;
      const computedStyle = window.getComputedStyle(container);
      const marginTop = parseFloat(computedStyle.marginTop) || 0;
      const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

      // Available height = viewport height - container top - margins - some padding for safety
      const availableHeight = viewportHeight - containerTop - marginTop - marginBottom - 32; // 32px safety padding

      // Cell height: minHeight from GridCell (200px) + gap (16px)
      const cellMinHeight = 200; // From GridCell minHeight
      const gap = 16; // theme.spacing(2) = 16px

      // Calculate how many rows fit: (availableHeight + gap) / (cellHeight + gap)
      // We add gap to availableHeight to account for the last row not having a gap below
      const rows = Math.floor((availableHeight + gap) / (cellMinHeight + gap));

      // Ensure at least 1 row, and enough rows to fit all items if they were placed sequentially
      const minRowsNeeded = Math.ceil(childrenArray.length / currentCols);
      const finalRows = Math.max(1, Math.max(rows, minRowsNeeded));

      if (finalRows !== calculatedRows) {
        setCalculatedRows(finalRows);
      }
    };

    // Calculate after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      calculateRows();
    }, 0);

    // Use ResizeObserver to recalculate when container or window size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateRows();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Observe parent element as well
    if (containerRef.current?.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', calculateRows);

    // Cleanup function for useEffect - returning cleanup is correct React pattern
    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateRows);
    };
  }, [fixedRows, calculatedRows, currentCols, childrenArray.length]);

  // Use fixed rows if provided, otherwise use calculated rows
  const rows = fixedRows !== undefined ? fixedRows : calculatedRows;

  // Generate unique IDs and initial positions for each child
  // Ensure positions are within grid bounds
  // Supports both configured props (gridX, gridY, gridW, gridH) and automatic sequential placement
  const initialLayouts: ItemLayout[] = useMemo(() => {
    const layouts: ItemLayout[] = [];
    const occupiedCells = new Set<string>();

    childrenArray.forEach((child, index) => {
      const key = React.isValidElement(child) && child.key ? String(child.key) : `item-${index}`;

      // Check if child has grid configuration props
      // Type guard for props with grid configuration
      interface GridConfigProps {
        gridX?: number;
        gridY?: number;
        gridW?: number;
        gridH?: number;
        locked?: boolean;
      }

      const props: GridConfigProps = React.isValidElement(child) ? (child.props as GridConfigProps) : {};

      const hasGridConfig =
        props.gridX !== undefined ||
        props.gridY !== undefined ||
        props.gridW !== undefined ||
        props.gridH !== undefined;

      let x: number;
      let y: number;
      let w: number;
      let h: number;

      if (hasGridConfig) {
        // Use configured position and size
        x = props.gridX !== undefined ? Math.max(0, Math.min(props.gridX, currentCols - 1)) : index % currentCols;
        y = props.gridY !== undefined ? Math.max(0, Math.min(props.gridY, rows - 1)) : Math.floor(index / currentCols);
        w = props.gridW !== undefined ? Math.max(1, Math.min(props.gridW, currentCols - x)) : 1;
        // For height, use the configured value directly, clamp to total available rows
        // Don't subtract y here - the height is absolute, not relative to position
        h = props.gridH !== undefined ? Math.max(1, Math.min(props.gridH, rows)) : 1;
      } else {
        // Backward compatibility: automatic sequential placement
        x = index % currentCols;
        y = Math.floor(index / currentCols);
        w = 1;
        h = 1;
      }

      // Check if position is available (for configured items, try to place them)
      // If position is occupied, fall back to next available position
      let finalX = x;
      let finalY = y;
      let attempts = 0;
      const maxAttempts = currentCols * rows;

      while (attempts < maxAttempts) {
        // Check if this position and size fits
        let fits = true;
        for (let dy = 0; dy < h; dy++) {
          for (let dx = 0; dx < w; dx++) {
            const cellKey = `${finalX + dx},${finalY + dy}`;
            if (occupiedCells.has(cellKey)) {
              fits = false;
              break;
            }
          }
          if (!fits) {
            break;
          }
        }

        if (fits) {
          // Mark cells as occupied
          for (let dy = 0; dy < h; dy++) {
            for (let dx = 0; dx < w; dx++) {
              occupiedCells.add(`${finalX + dx},${finalY + dy}`);
            }
          }
          break;
        }

        // Try next position (for configured items, try to find nearby available space)
        if (hasGridConfig) {
          // For configured items, try positions near the target
          finalX = (finalX + 1) % currentCols;
          if (finalX === 0) {
            finalY = (finalY + 1) % rows;
          }
        } else {
          // For auto-placed items, use sequential placement
          finalX = (finalX + w) % currentCols;
          if (finalX + w > currentCols) {
            finalX = 0;
            finalY = Math.min(finalY + 1, rows - 1);
          }
        }
        attempts += 1;
      }

      // Clamp to grid bounds - initial size (gridW/gridH) is only a starting point, not a constraint
      finalX = Math.min(finalX, currentCols - 1);
      finalY = Math.min(finalY, rows - 1);
      // Clamp width based on final position and grid bounds (not limited by originalW)
      w = Math.min(w, currentCols - finalX);
      // Clamp height based on final position - ensure it fits from finalY to bottom of grid
      // Only reduce height if it would exceed grid bounds (not limited by originalH)
      const maxHeightFromPosition = rows - finalY;
      h = Math.min(h, maxHeightFromPosition);

      layouts.push({
        id: key,
        position: {
          x: finalX,
          y: finalY,
        },
        w,
        h,
        // Constraints are independent of initial gridW/gridH values
        // Users can resize freely between min and max bounds
        minW: 1,
        minH: 1,
        maxW: currentCols,
        maxH: rows,
        locked: props.locked !== undefined ? props.locked : true, // Use prop if provided, default to true
      });
    });

    return layouts;
  }, [childrenArray, currentCols, rows]);

  const [layouts, setLayouts] = useState<ItemLayout[]>(initialLayouts);
  const [overId, setOverId] = useState<string | null>(null);

  // Sync with external layouts when provided
  useEffect(() => {
    if (externalLayouts && externalLayouts.length > 0) {
      setLayouts(prevLayouts => {
        // Only update if layouts actually changed to avoid infinite loops
        const prevStr = JSON.stringify(prevLayouts);
        const externalStr = JSON.stringify(externalLayouts);
        if (prevStr !== externalStr) {
          return externalLayouts;
        }
        return prevLayouts;
      });
    }
  }, [externalLayouts]);

  // Helper function to get all cells occupied by an item
  const getOccupiedCells = (layout: ItemLayout): string[] => {
    const cells: string[] = [];
    const w = layout.w || 1;
    const h = layout.h || 1;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        cells.push(`${layout.position.x + dx},${layout.position.y + dy}`);
      }
    }
    return cells;
  };

  // Helper function to check if a position is available for an item
  const isPositionAvailable = (position: GridPosition, w: number, h: number, excludeId?: string): boolean => {
    // Check bounds
    if (position.x + w > currentCols || position.y + h > rows) {
      return false;
    }

    // Check for collisions with other items
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const cellX = position.x + dx;
        const cellY = position.y + dy;
        const cellKey = `${cellX},${cellY}`;

        // Check if any other item occupies this cell
        const occupyingItem = layouts.find(
          layout => layout.id !== excludeId && getOccupiedCells(layout).includes(cellKey)
        );

        if (occupyingItem) {
          return false;
        }
      }
    }

    return true;
  };

  // Handle lock toggle
  const handleLockToggle = (id: string) => {
    setLayouts(currentLayouts => {
      const newLayouts = currentLayouts.map(layout =>
        layout.id === id ? { ...layout, locked: !layout.locked } : layout
      );

      if (onLayoutChange) {
        onLayoutChange(newLayouts);
      }

      return newLayouts;
    });
  };

  // Handle resize
  const handleResize = (id: string, newW: number, newH: number) => {
    setLayouts(currentLayouts => {
      const itemIndex = currentLayouts.findIndex(l => l.id === id);
      if (itemIndex === -1) {
        return currentLayouts;
      }

      const item = currentLayouts[itemIndex];
      const updatedItem: ItemLayout = {
        ...item,
        w: newW,
        h: newH,
      };

      // Check if the new size fits at current position
      if (isPositionAvailable(item.position, newW, newH, id)) {
        const newLayouts = [...currentLayouts];
        newLayouts[itemIndex] = updatedItem;

        // Notify parent of layout change
        if (onLayoutChange) {
          onLayoutChange(newLayouts);
        }

        return newLayouts;
      }

      // If it doesn't fit, try to find a new position
      // For now, just clamp the size to what fits
      let clampedW = newW;
      let clampedH = newH;

      // Clamp width
      while (clampedW > 1 && !isPositionAvailable(item.position, clampedW, clampedH, id)) {
        clampedW -= 1;
      }

      // Clamp height
      while (clampedH > 1 && !isPositionAvailable(item.position, clampedW, clampedH, id)) {
        clampedH -= 1;
      }

      const newLayouts = [...currentLayouts];
      newLayouts[itemIndex] = {
        ...updatedItem,
        w: clampedW,
        h: clampedH,
      };

      // Notify parent of layout change
      if (onLayoutChange) {
        onLayoutChange(newLayouts);
      }

      return newLayouts;
    });
  };

  // Reflow items when columns change - move items to next row if no space
  React.useEffect(() => {
    setLayouts(currentLayouts => {
      // Sort items by their current position (row first, then column) to maintain order
      const sortedLayouts = [...currentLayouts].sort((a, b) => {
        if (a.position.y !== b.position.y) {
          return a.position.y - b.position.y;
        }
        return a.position.x - b.position.x;
      });

      // Place items row by row, left to right, in the new grid
      const newLayouts: ItemLayout[] = [];
      let currentX = 0;
      let currentY = 0;

      sortedLayouts.forEach(layout => {
        const itemW = layout.w || 1;
        const itemH = layout.h || 1;

        // If item doesn't fit in current row, move to next row
        if (currentX + itemW > currentCols) {
          currentX = 0;
          currentY += 1;
        }

        // If we've exceeded max rows, wrap to beginning (shouldn't happen, but safety check)
        if (currentY + itemH > rows) {
          currentY = 0;
          currentX = 0;
        }

        // Clamp item size to fit in grid
        const clampedW = Math.min(itemW, currentCols - currentX);
        const clampedH = Math.min(itemH, rows - currentY);

        newLayouts.push({
          ...layout,
          position: { x: currentX, y: currentY },
          w: clampedW,
          h: clampedH,
        });

        // Move to next position (account for item width)
        currentX += clampedW;
      });

      // Only notify parent if layouts actually changed
      const layoutsChanged = JSON.stringify(newLayouts) !== JSON.stringify(currentLayouts);
      if (layoutsChanged && onLayoutChange) {
        onLayoutChange(newLayouts);
      }

      return newLayouts;
    });
  }, [currentCols, rows, onLayoutChange]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Create a map for quick lookup
  const itemsMap = useMemo(() => {
    const map = new Map<string, ReactNode>();
    childrenArray.forEach((child, index) => {
      const key = React.isValidElement(child) && child.key ? String(child.key) : `item-${index}`;
      map.set(key, child);
    });
    return map;
  }, [childrenArray]);

  const handleDragStart = () => {
    // Drag started - could be used for visual feedback
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (over) {
      setOverId(over.id as string);
    } else {
      setOverId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const draggedItemId = active.id as string;

    // Get the dragged item
    const draggedItem = layouts.find(l => l.id === draggedItemId);
    if (!draggedItem) {
      return;
    }

    // Check if the dragged item is locked
    if (draggedItem.locked) {
      return; // Don't allow drag if item is locked
    }

    // Get the target position from the droppable cell
    const overData = over.data.current;
    if (!overData?.position) {
      return;
    }

    const targetPosition: GridPosition = {
      x: Math.min(Math.max(overData.position.x, 0), currentCols - 1),
      y: Math.min(Math.max(overData.position.y, 0), rows - 1),
    };

    const itemW = draggedItem.w || 1;
    const itemH = draggedItem.h || 1;

    // Check if target position is available for the item's size
    const isAvailable = isPositionAvailable(targetPosition, itemW, itemH, draggedItemId);

    // Find the item that's occupying the target position (if any)
    const occupyingItem = layouts.find(
      layout =>
        layout.id !== draggedItemId &&
        getOccupiedCells(layout).some(cell => cell === `${targetPosition.x},${targetPosition.y}`)
    );

    setLayouts(currentLayouts => {
      const newLayouts = [...currentLayouts];
      const activeIndex = newLayouts.findIndex(l => l.id === draggedItemId);

      if (activeIndex === -1) {
        return currentLayouts;
      }

      if (isAvailable) {
        // Move to empty position
        newLayouts[activeIndex].position = targetPosition;
      } else if (occupyingItem) {
        // Try to swap positions if both items fit
        const occupyingIndex = newLayouts.findIndex(l => l.id === occupyingItem.id);
        const occupyingW = occupyingItem.w || 1;
        const occupyingH = occupyingItem.h || 1;
        const activePosition = newLayouts[activeIndex].position;

        // Check if swap is possible
        if (
          isPositionAvailable(activePosition, occupyingW, occupyingH, occupyingItem.id) &&
          isPositionAvailable(targetPosition, itemW, itemH, draggedItemId)
        ) {
          newLayouts[activeIndex].position = targetPosition;
          newLayouts[occupyingIndex].position = activePosition;
        }
      }

      if (onLayoutChange) {
        onLayoutChange(newLayouts);
      }

      return newLayouts;
    });
  };

  // Generate all grid cells (occupied and empty) - fixed grid size
  const allCells: Array<{ position: GridPosition; itemId?: string }> = useMemo(() => {
    const cells: Array<{ position: GridPosition; itemId?: string }> = [];
    const occupiedMap = new Map<string, string>();

    // Map all occupied positions (accounting for item sizes)
    layouts.forEach(layout => {
      const occupiedCells = getOccupiedCells(layout);
      occupiedCells.forEach(cellKey => {
        occupiedMap.set(cellKey, layout.id);
      });
    });

    // Generate all cells in the fixed grid (rows × cols)
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < currentCols; x++) {
        const posKey = `${x},${y}`;
        const itemId = occupiedMap.get(posKey);
        cells.push({
          position: { x, y },
          itemId: itemId || undefined,
        });
      }
    }

    return cells;
  }, [layouts, rows, currentCols]);

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      <GridContainer ref={containerRef} className={className} cols={currentCols} rows={rows}>
        {/* Render all cells (empty and occupied) */}
        {allCells.map(cell => {
          const cellId = `cell-${cell.position.x}-${cell.position.y}`;
          const isEmpty = !cell.itemId;
          const isOver = overId === cellId;

          if (cell.itemId && itemsMap.has(cell.itemId)) {
            // Only render item at its top-left position
            const layout = layouts.find(l => l.id === cell.itemId);
            if (layout && layout.position.x === cell.position.x && layout.position.y === cell.position.y) {
              return (
                <DraggableItem
                  key={cell.itemId}
                  id={cell.itemId}
                  position={layout.position}
                  w={layout.w || 1}
                  h={layout.h || 1}
                  onResize={handleResize}
                  onLockToggle={handleLockToggle}
                  cols={currentCols}
                  rows={rows}
                  minW={layout.minW || 1}
                  minH={layout.minH || 1}
                  maxW={layout.maxW || currentCols}
                  maxH={layout.maxH || rows}
                  locked={layout.locked !== false} // Default to true if not specified
                >
                  {itemsMap.get(cell.itemId)}
                </DraggableItem>
              );
            }
            return null;
          }

          // Render empty droppable cell
          return <DroppableCell key={cellId} id={cellId} position={cell.position} isEmpty={isEmpty} isOver={isOver} />;
        })}
      </GridContainer>
    </DndContext>
  );
};

export default DraggableChartGrid;
